import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import DatePicker from "../../components/DatePicker";
import Select from "../../components/Select";
import { useCompanyDefaults, resolveEmployeeWorkSettings } from "../../utils/companyDefaults";
import styles from "./PeoplePage.module.css";

// ── Helpers for the override + leaves / earnings / deductions fields ──
// Kept local to PeoplePage since they're tightly coupled to the modal.

const OVERTIME_START_OPTIONS = [
  { value: 0,   label: "Immediately (0 min)" },
  { value: 15,  label: "15 minutes after shift end" },
  { value: 30,  label: "30 minutes after shift end" },
  { value: 45,  label: "45 minutes after shift end" },
  { value: 60,  label: "1 hour after shift end" },
  { value: 90,  label: "1 hour 30 minutes after shift end" },
  { value: 120, label: "2 hours after shift end" },
];

const WORK_TIME_POLICY_OPTIONS = [
  { value: "STRICT",   label: "Strict Schedule" },
  { value: "FLEXIBLE", label: "Flexible Hours" },
];

const SALARY_TYPE_OPTIONS = [
  { value: "monthly", label: "Monthly Salaried — full base every month; only unpaid absences deduct" },
  { value: "daily",   label: "Daily Rate — pay accrues per day clocked in (₱rate × days worked)" },
];

function parseLeaves(raw) {
  if (!raw) return [];
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(d)) return [];
    return d.map(o => ({
      name: String(o?.name || ""),
      days: o?.days != null ? String(o.days) : "",
      convertible: !!o?.convertible,
    })).filter(l => l.name);
  } catch { return []; }
}

function parseEarnings(raw) {
  if (!raw) return [];
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(d)) return [];
    return d.map(o => ({
      name: String(o?.name || ""),
      value: o?.value != null ? String(o.value) : "",
    })).filter(r => r.name);
  } catch { return []; }
}

function parseDeductions(raw) {
  const empty = { sss: "", philhealth: "", pagibig: "", insurance: "", others: [] };
  if (!raw) return empty;
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!d || typeof d !== "object") return empty;
    return {
      sss:        d.sss        != null ? String(d.sss)        : "",
      philhealth: d.philhealth != null ? String(d.philhealth) : "",
      pagibig:    d.pagibig    != null ? String(d.pagibig)    : "",
      insurance:  d.insurance  != null ? String(d.insurance)  : "",
      others: Array.isArray(d.others) ? d.others.map(o => ({
        name: String(o?.name || ""),
        value: o?.value != null ? String(o.value) : "",
      })).filter(r => r.name) : [],
    };
  } catch { return empty; }
}

/** Display row for the "Reporting To" dropdown — Name — Role · Department. */
function reportsToLabel(e) {
  const role = e?.role ? ` — ${e.role}` : "";
  const dep  = e?.department ? ` · ${e.department}` : "";
  return `${e?.name || e?.code || "—"}${role}${dep}`;
}

/** Merge company-default leaves into a per-employee list. Defaults
 *  whose names already appear on the employee (case-insensitive) are
 *  dropped — the employee row wins. */
function mergeLeavesWithDefaults(empLeaves, defaultLeaves) {
  const taken = new Set(empLeaves.map(l => (l.name || "").trim().toLowerCase()));
  const fromDefaults = (defaultLeaves || []).filter(d =>
    d.name && !taken.has(d.name.trim().toLowerCase())
  );
  return { empLeaves, fromDefaults };
}

// Pagination retired — the staff list now scrolls within a viewport-aware
// max-height (see .tableWrap in PeoplePage.module.css). At ≤500 rows this
// is smoother than paging; if a school ever crosses ~1000 staff, swap in
// virtualization (e.g. react-window) before re-introducing pagination.
const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function StatusBadge({ status }) {
  if (!status) return <span style={{ color: "rgba(0,0,0,.35)" }}>—</span>;
  const s = status.toLowerCase();
  const cls = s === "active" ? styles.statusActive
            : s === "leave"  ? styles.statusLeave
            : styles.statusInactive;
  return <span className={`${styles.statusBadge} ${cls}`}>{status}</span>;
}

const EMPTY_FORM = {
  name: "", role: "", staffType: "", department: "", salary: "", status: "Active",
  hireDate: "",
  reportsTo: "",
  // Per-employee work-settings overrides. Null = inherit company default.
  // Toggled together by the "Override company default work settings"
  // checkbox in the modal.
  expectedStart: null,
  expectedEnd: null,
  overtimeStartMinutes: null,
  workTimePolicy: "",
  applyDeductionsOnDayOff: null,
  salaryType: "",
  // JSON-backed sections — UI manages these as arrays / objects, then
  // serialises before sending to the backend.
  leaves: [],            // [{ name, days, convertible }]
  earnings: [],          // [{ name, value }]
  deductions: { sss: "", philhealth: "", pagibig: "", insurance: "", others: [] },
  birthday: "", phone: "", email: "", citizenship: "", city: "", address: "",
};

/** Staff-type options. Stored as `staff_type` on the backend.
 * MANAGEMENT + TEACHING both appear in Teacher / Adviser / Coach / Faculty
 * Head pickers in Academics. Finer-grained job titles live in the Role
 * dropdown (Positions tab) and departments on the Department dropdown. */
const STAFF_TYPES = [
  { value: "",             label: "— Select a type —" },
  { value: "MANAGEMENT",   label: "Management" },
  { value: "TEACHING",     label: "Teaching" },
  { value: "NON_TEACHING", label: "Non-Teaching" },
  { value: "SUPPORT",      label: "Support Staff" },
];

const DOC_TYPES = [
  { key: "contract", label: "Contract", tag: "P", type: "pdf", color: "#2b6cb0" },
  { key: "resume",   label: "Resume",   tag: "P", type: "pdf",  color: "#c0392b" },
  { key: "cv",       label: "CV",       tag: "P", type: "pdf",  color: "#6c3483" },
];

function EmployeeModal({ emp, onClose, onSaved }) {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile]       = useState(null);
  const [docFiles, setDocFiles]         = useState({}); // { contract: File, resume: File, cv: File }
  const [existingDocs, setExistingDocs] = useState([]);
  const [departments, setDepartments]   = useState([]);
  const [positions,   setPositions]     = useState([]);
  // Pool for the Manager picker. Includes everyone, then we filter the
  // current employee out at render time so a person can't be their own
  // manager.
  const [allEmployees, setAllEmployees] = useState([]);
  const companyDefaults = useCompanyDefaults();
  const resolvedDefaults = useMemo(
    () => resolveEmployeeWorkSettings(null, companyDefaults),
    [companyDefaults],
  );
  const defaultLeaveRows = useMemo(
    () => parseLeaves(companyDefaults["work.defaultLeaves"]),
    [companyDefaults],
  );

  // The override checkbox is checked when ANY of the per-employee
  // work-time fields is set. Toggling it ON seeds all six fields from
  // resolvedDefaults; toggling OFF clears them all back to null.
  const overriding = !!(
    form.expectedStart || form.expectedEnd ||
    (form.overtimeStartMinutes != null && form.overtimeStartMinutes !== "") ||
    form.workTimePolicy ||
    form.applyDeductionsOnDayOff != null ||
    form.salaryType
  );
  function toggleOverride(on) {
    if (on) {
      setForm(f => ({
        ...f,
        expectedStart:           resolvedDefaults.schedStart,
        expectedEnd:             resolvedDefaults.schedEnd,
        overtimeStartMinutes:    resolvedDefaults.overtimeStartMinutes,
        workTimePolicy:          resolvedDefaults.workTimePolicy,
        applyDeductionsOnDayOff: !!resolvedDefaults.applyDeductionsOnDayOff,
        salaryType:              resolvedDefaults.salaryType || "monthly",
      }));
    } else {
      setForm(f => ({
        ...f,
        expectedStart: null, expectedEnd: null,
        overtimeStartMinutes: null, workTimePolicy: "",
        applyDeductionsOnDayOff: null, salaryType: "",
      }));
    }
  }

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/departments`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (!cancelled) setDepartments(Array.isArray(list) ? list : []); })
      .catch(() => {});
    fetch(`${API_BASE}/api/positions`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (!cancelled) setPositions(Array.isArray(list) ? list : []); })
      .catch(() => {});
    fetch(`${API_BASE}/api/employees?all=true`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (!cancelled) setAllEmployees(Array.isArray(list) ? list : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [otherDocs, setOtherDocs]       = useState([]); // [{ name: string, file: File|null }]
  const [existingOtherDocs, setExistingOtherDocs] = useState([]);
  const isEdit     = !!emp;
  const overlayRef = useRef(null);
  const photoRef   = useRef(null);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!emp) { setForm(EMPTY_FORM); setPhotoPreview(null); setPhotoFile(null); setDocFiles({}); setExistingDocs([]); setOtherDocs([]); setExistingOtherDocs([]); return; }
    const base = {
      name:        emp.name        || "",
      role:        emp.role        || "",
      staffType:   emp.staffType   || "",
      department:  emp.department  || "",
      salary:      emp.salary != null ? String(emp.salary) : "",
      status:      emp.status      || "Active",
      hireDate:    emp.hireDate    || "",
      reportsTo:   emp.reportsTo   || "",
      expectedStart:           emp.expectedStart        ?? null,
      expectedEnd:             emp.expectedEnd          ?? null,
      overtimeStartMinutes:    emp.overtimeStartMinutes ?? null,
      workTimePolicy:          emp.workTimePolicy       || "",
      applyDeductionsOnDayOff: emp.applyDeductionsOnDayOff ?? null,
      salaryType:              emp.salaryType           || "",
      leaves:     parseLeaves(emp.leavesJson),
      earnings:   parseEarnings(emp.earningsJson),
      deductions: parseDeductions(emp.deductionsJson),
      birthday: "", phone: "", email: "", citizenship: "", city: "", address: "",
    };
    setForm(base);
    setPhotoPreview(emp.photoUrl || null);
    setPhotoFile(null);
    setDocFiles({});
    setOtherDocs([]);

    // Fetch profile + documents
    Promise.all([
      fetch(`${API_BASE}/api/employees/${emp.code}/profile`).then(r => r.ok ? r.json() : {}),
      fetch(`${API_BASE}/api/employees/${emp.code}/documents`).then(r => r.ok ? r.json() : []),
    ]).then(([p, docs]) => {
      setForm(f => ({
        ...f,
        birthday:    p.birthday    || "",
        phone:       p.phone       || "",
        email:       p.email       || "",
        citizenship: p.citizenship || "",
        city:        p.city        || "",
        address:     p.address     || "",
      }));
      const allDocs = Array.isArray(docs) ? docs : [];
      const knownLabels = DOC_TYPES.map(d => d.label);
      setExistingDocs(allDocs.filter(d => knownLabels.includes(d.name)));
      setExistingOtherDocs(allDocs.filter(d => !knownLabels.includes(d.name)));
    }).catch(() => {});
  }, [emp]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function onPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function onDocChange(key, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocFiles(d => ({ ...d, [key]: file }));
  }

  function removeDoc(key) { setDocFiles(d => { const n = { ...d }; delete n[key]; return n; }); }

  function addOtherDoc() { setOtherDocs(d => [...d, { name: "", file: null }]); }
  function updateOtherDoc(idx, field, value) {
    setOtherDocs(d => d.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  }
  function removeOtherDoc(idx) { setOtherDocs(d => d.filter((_, i) => i !== idx)); }

  async function save() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError("");
    try {
      const code = isEdit
        ? emp.code
        : form.name.trim().toLowerCase().split(/\s+/)[0] + "_" + Date.now();

      // Serialise the JSON-backed sections. Empty payloads send null so
      // the column clears cleanly instead of holding "[]".
      const cleanLeaves = form.leaves
        .map(l => ({ name: (l.name || "").trim(), days: Number(l.days) || 0, convertible: !!l.convertible }))
        .filter(l => l.name);
      const cleanEarnings = form.earnings
        .map(r => ({ name: (r.name || "").trim(), value: Number(r.value) || 0 }))
        .filter(r => r.name);
      const d = form.deductions || {};
      const cleanDeductions = {
        sss:        Number(d.sss)        || 0,
        philhealth: Number(d.philhealth) || 0,
        pagibig:    Number(d.pagibig)    || 0,
        insurance:  Number(d.insurance)  || 0,
        others: (d.others || [])
          .map(o => ({ name: (o.name || "").trim(), value: Number(o.value) || 0 }))
          .filter(o => o.name),
      };
      const allZero = cleanDeductions.sss === 0 && cleanDeductions.philhealth === 0
        && cleanDeductions.pagibig === 0 && cleanDeductions.insurance === 0
        && cleanDeductions.others.length === 0;

      const empBody = {
        name:       form.name.trim(),
        role:       form.role.trim(),
        staffType:  form.staffType || null,
        // Faculty→Department merge: every staff type now uses the
        // department string. facultyId is sent as null so the backend
        // clears any legacy value left over from the old dual-key model.
        department: form.department || null,
        facultyId:  null,
        salary:     form.salary !== "" ? Number(form.salary) : null,
        status:     form.status,
        hireDate:   form.hireDate || null,
        reportsTo:  form.reportsTo || null,
        expectedStart:           form.expectedStart || null,
        expectedEnd:             form.expectedEnd   || null,
        overtimeStartMinutes:    form.overtimeStartMinutes != null && form.overtimeStartMinutes !== ""
                                   ? Number(form.overtimeStartMinutes) : null,
        workTimePolicy:          form.workTimePolicy || null,
        applyDeductionsOnDayOff: form.applyDeductionsOnDayOff,
        salaryType:              form.salaryType || null,
        leavesJson:    cleanLeaves.length    ? JSON.stringify(cleanLeaves)    : null,
        earningsJson:  cleanEarnings.length  ? JSON.stringify(cleanEarnings)  : null,
        deductionsJson: allZero              ? null                            : JSON.stringify(cleanDeductions),
      };

      if (isEdit) {
        await fetch(`${API_BASE}/api/employees/${code}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(empBody),
        });
      } else {
        await fetch(`${API_BASE}/api/employees`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...empBody, code, pct: 50 }),
        });
      }

      // Save profile
      const profileBody = {
        birthday:    form.birthday.trim()    || null,
        phone:       form.phone.trim()       || null,
        email:       form.email.trim()       || null,
        citizenship: form.citizenship.trim() || null,
        city:        form.city.trim()        || null,
        address:     form.address.trim()     || null,
      };
      await fetch(`${API_BASE}/api/employees/${code}/profile`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileBody),
      });

      // Upload photo if changed
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await fetch(`${API_BASE}/api/employees/${code}/photo`, { method: "POST", body: fd });
      }

      // Upload new documents
      for (const dt of DOC_TYPES) {
        const file = docFiles[dt.key];
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", dt.label);
        fd.append("type", dt.type);
        fd.append("tag",  dt.tag);
        await fetch(`${API_BASE}/api/employees/${code}/documents`, { method: "POST", body: fd });
      }

      // Upload other documents
      for (const od of otherDocs) {
        if (!od.file || !od.name.trim()) continue;
        const fd = new FormData();
        fd.append("file", od.file);
        fd.append("name", od.name.trim());
        fd.append("type", od.file.name.split(".").pop() || "pdf");
        fd.append("tag", od.name.trim().charAt(0).toUpperCase());
        await fetch(`${API_BASE}/api/employees/${code}/documents`, { method: "POST", body: fd });
      }

      onSaved();
    } catch (e) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // Portaled to <body> so the fixed-position overlay anchors to the
  // viewport rather than to .shell (whose transform creates a fixed-
  // positioning containing block — without this, scrolling the staff
  // list before clicking Edit would leave the modal off-screen).
  return createPortal(
    <div className={styles.overlay} ref={overlayRef}>
      <div className={styles.modal}>

        {/* Modal header */}
        <div className={styles.modalHeader}>
          {/* Photo upload */}
          <div className={styles.photoWrap} onClick={() => photoRef.current?.click()} title="Click to change photo">
            {photoPreview
              ? <img src={photoPreview} alt="photo" className={styles.photoImg} />
              : <div className={styles.modalAvatar} style={{ background: avatarColor(form.name || "?") }}>{initials(form.name || "?")}</div>
            }
            <div className={styles.photoOverlay}>📷</div>
            <input ref={photoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onPhotoChange} />
          </div>
          <div>
            <div className={styles.modalTitle}>{isEdit ? form.name || "Edit Staff Member" : "Add Staff Member"}</div>
            <div className={styles.modalSubtitle}>{isEdit ? emp.code : "New staff member"}</div>
          </div>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modalBody}>
          {/* Work Information */}
          <div className={styles.modalSection}>Work Information</div>
          <div className={styles.modalGrid}>
            <div className={styles.field}>
              <label className={styles.label}>Full Name *</label>
              <input className={styles.input} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Harry Bender" />
            </div>

            {/* ── Cascade: Staff Type → Department (or Faculty) → Role ──
               Each field unlocks the next. Disabled steps show a hint so
               admins know why the dropdown is greyed out. */}

            {/* 1. Staff Type — always enabled, drives everything below */}
            <div className={styles.field}>
              <label className={styles.label}>Staff Type *</label>
              <Select
                value={form.staffType}
                onChange={v => {
                  // Switching the staff type always resets the dependent
                  // choices — they only make sense in the context of a type.
                  setForm(f => ({
                    ...f,
                    staffType:  v,
                    role:       "",
                    department: "",
                  }));
                }}
                options={[
                  ...STAFF_TYPES.filter(t => t.value).map(t => ({ value: t.value, label: t.label })),
                  ...(form.staffType && !STAFF_TYPES.some(t => t.value === form.staffType)
                    ? [{ value: form.staffType, label: `${form.staffType} (legacy)` }]
                    : []),
                ]}
                placeholder="— Select a type —"
                ariaLabel="Staff Type"
              />
            </div>

            {/* 2. Department — locked until Staff Type is picked. The
                   Faculty/Department split was retired in the
                   Faculty→Department merge: every staff type now picks
                   from departments filtered by the chosen staffType, and
                   academic levels (Pre-K, JHS, SHS, Grade School) live
                   in the same table tagged staffType=TEACHING. */}
            {(() => {
              const filteredDepartments = form.staffType
                ? departments.filter(d => d.staffType === form.staffType)
                : departments;
              const deptOptions = [
                ...filteredDepartments.map(d => ({ value: d.name, label: d.name })),
                ...(form.department && !filteredDepartments.some(d => d.name === form.department)
                  ? [{ value: form.department, label: `${form.department} (other)` }]
                  : []),
              ];
              return (
                <div className={styles.field}>
                  <label className={styles.label}>Department *</label>
                  <Select
                    value={form.department}
                    onChange={v => {
                      // New department implies a new set of available roles —
                      // reset role to force an explicit re-pick.
                      setForm(f => ({ ...f, department: v, role: "" }));
                    }}
                    options={deptOptions}
                    placeholder={form.staffType ? "— Select a department —" : "Select staff type first"}
                    disabled={!form.staffType}
                    hint={
                      !form.staffType
                        ? "Pick a staff type above to enable."
                        : `${filteredDepartments.length} department${filteredDepartments.length === 1 ? "" : "s"} available`
                    }
                    ariaLabel="Department"
                  />
                </div>
              );
            })()}

            {/* 3. Position — locked until Department is picked. Position
                   list = "all positions belonging to the picked
                   department". For TEACHING staff we widen this to "all
                   positions across every TEACHING department" so a
                   teacher in Junior High School can still pick the
                   "Teacher" position seeded under Grade School. */}
            {(() => {
              const selectedDept = form.department
                ? departments.find(d => d.name === form.department)
                : null;
              const teachingDeptIds = new Set(
                departments.filter(d => d.staffType === "TEACHING").map(d => d.id)
              );
              const filteredPositions = positions.filter(p => {
                if (form.staffType === "TEACHING") {
                  return teachingDeptIds.has(p.departmentId);
                }
                if (!selectedDept) return false;
                return p.departmentId === selectedDept.id;
              });
              const needsDept = !form.department;
              const locked    = !form.staffType || needsDept;
              const placeholder  =
                !form.staffType ? "Select staff type first"
                : needsDept     ? "Select department first"
                :                 "— Select a position —";
              const hintLine =
                !form.staffType ? "Pick a staff type above to enable."
                : needsDept     ? "Pick a department above to enable."
                :                 `${filteredPositions.length} position${filteredPositions.length === 1 ? "" : "s"} available`;
              const roleOptions = [
                ...filteredPositions.map(p => ({ value: p.name, label: p.name })),
                ...(form.role && !filteredPositions.some(p => p.name === form.role)
                  ? [{ value: form.role, label: `${form.role} (other)` }]
                  : []),
              ];
              return (
                <div className={styles.field}>
                  <label className={styles.label}>Position *</label>
                  <Select
                    value={form.role}
                    onChange={v => set("role", v)}
                    options={roleOptions}
                    placeholder={placeholder}
                    disabled={locked}
                    hint={hintLine}
                    ariaLabel="Position"
                  />
                </div>
              );
            })()}
            <div className={styles.field}>
              <label className={styles.label}>Salary ($)</label>
              <input className={styles.input} type="number" min="0" value={form.salary} onChange={e => set("salary", e.target.value)} placeholder="e.g. 5800" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Status</label>
              <select className={styles.input} value={form.status} onChange={e => set("status", e.target.value)}>
                <option value="Active">Active</option>
                <option value="Leave">Leave</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Hire Date</label>
              <input
                className={styles.input}
                type="date"
                value={form.hireDate || ""}
                onChange={e => set("hireDate", e.target.value)}
              />
            </div>
            {/* Manager — drives the leave-approval queue. The selected
                manager (and Admin/SuperAdmin) can decide this employee's
                leave requests on the Calendar page. Optional; admin tier
                always decides for unassigned employees. */}
            <div className={styles.field}>
              <label className={styles.label}>Reporting To</label>
              <select
                className={styles.input}
                value={form.reportsTo}
                onChange={e => set("reportsTo", e.target.value)}
              >
                <option value="">— None —</option>
                {allEmployees
                  .filter(e => !isEdit || e.code !== emp.code)
                  .map(e => (
                    <option key={e.code} value={e.code}>{reportsToLabel(e)}</option>
                  ))}
              </select>
              <div className={styles.cascadeHint}>
                Approves this employee's leave / overtime requests.
              </div>
            </div>
          </div>

          {/* Override Company Default Work Settings */}
          <div style={{ marginTop: 18, padding: "14px 16px", background: "#fafaf6",
                        border: "1px solid #ece5d2", borderRadius: 12 }}>
            <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer", fontWeight: 700, color: "#3d342a" }}>
              <input
                type="checkbox"
                checked={overriding}
                onChange={e => toggleOverride(e.target.checked)}
              />
              Override company default work settings
            </label>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#5a4e3a", fontWeight: 600 }}>Expected Scheduled Start</span>
                <input
                  className={styles.input}
                  type="time"
                  disabled={!overriding}
                  value={form.expectedStart || resolvedDefaults.schedStart || ""}
                  onChange={e => set("expectedStart", e.target.value)}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#5a4e3a", fontWeight: 600 }}>Expected Scheduled End</span>
                <input
                  className={styles.input}
                  type="time"
                  disabled={!overriding}
                  value={form.expectedEnd || resolvedDefaults.schedEnd || ""}
                  onChange={e => set("expectedEnd", e.target.value)}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#5a4e3a", fontWeight: 600 }}>Overtime Start</span>
                <select
                  className={styles.input}
                  disabled={!overriding}
                  value={String(form.overtimeStartMinutes ?? resolvedDefaults.overtimeStartMinutes ?? 30)}
                  onChange={e => set("overtimeStartMinutes", e.target.value === "" ? null : Number(e.target.value))}>
                  {OVERTIME_START_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#5a4e3a", fontWeight: 600 }}>Work Time Policy</span>
                <select
                  className={styles.input}
                  disabled={!overriding}
                  value={form.workTimePolicy || resolvedDefaults.workTimePolicy || "STRICT"}
                  onChange={e => set("workTimePolicy", e.target.value)}>
                  {WORK_TIME_POLICY_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 12, cursor: overriding ? "pointer" : "default", opacity: overriding ? 1 : 0.6 }}>
              <input
                type="checkbox"
                disabled={!overriding}
                checked={form.applyDeductionsOnDayOff != null
                  ? !!form.applyDeductionsOnDayOff
                  : !!resolvedDefaults.applyDeductionsOnDayOff}
                onChange={e => set("applyDeductionsOnDayOff", e.target.checked)}
              />
              <span style={{ fontSize: 13, color: "#3d342a" }}>
                Apply late / undertime deductions on weekends and holidays
              </span>
            </label>
            {!overriding && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#8a8070" }}>
                (using company default — tick the override checkbox above to customise)
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
              <span style={{ fontSize: 12, color: "#5a4e3a", fontWeight: 600 }}>
                Salary Type {!overriding && <span style={{ color: "#8a8070", fontWeight: 400 }}>
                  (using company default — {resolvedDefaults.salaryType === "daily" ? "Daily" : "Monthly"})
                </span>}
              </span>
              <select
                className={styles.input}
                disabled={!overriding}
                value={form.salaryType || resolvedDefaults.salaryType || "monthly"}
                onChange={e => set("salaryType", e.target.value)}>
                <option value="">Use company default</option>
                {SALARY_TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Leave Benefits */}
          <div className={styles.modalSection}>Leave Benefits</div>
          {(() => {
            const { empLeaves, fromDefaults } = mergeLeavesWithDefaults(form.leaves, defaultLeaveRows);
            return (
              <>
                {fromDefaults.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#8a8070",
                                  letterSpacing: ".5px", textTransform: "uppercase", marginBottom: 6 }}>
                      From Company Defaults
                    </div>
                    {fromDefaults.map((d, idx) => (
                      <div key={`def-${idx}`} style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8, opacity: .85 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 }}>
                          <input className={styles.input} value={d.name} disabled />
                          <input className={styles.input} value={d.days} disabled />
                        </div>
                        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#5a4e3a" }}>
                          <input type="checkbox" checked={!!d.convertible} disabled />
                          Convertible to cash at end of year
                        </label>
                      </div>
                    ))}
                  </>
                )}
                {empLeaves.length > 0 && fromDefaults.length > 0 && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#8a8070",
                                letterSpacing: ".5px", textTransform: "uppercase", margin: "10px 0 6px" }}>
                    Added Manually
                  </div>
                )}
                {empLeaves.map((row, idx) => (
                  <div key={`emp-${idx}`} style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 36px", gap: 8 }}>
                      <input
                        className={styles.input}
                        placeholder="Sick Leave"
                        value={row.name}
                        onChange={e => setForm(f => ({ ...f, leaves: f.leaves.map((r, i) => i === idx ? { ...r, name: e.target.value } : r) }))}
                      />
                      <input
                        className={styles.input}
                        type="number" min="0"
                        placeholder="Days"
                        value={row.days}
                        onChange={e => setForm(f => ({ ...f, leaves: f.leaves.map((r, i) => i === idx ? { ...r, days: e.target.value } : r) }))}
                      />
                      <button
                        type="button"
                        title="Remove"
                        onClick={() => setForm(f => ({ ...f, leaves: f.leaves.filter((_, i) => i !== idx) }))}
                        style={{ background: "#fee2e2", border: "1px solid #fecaca",
                                 color: "#991b1b", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>×</button>
                    </div>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#5a4e3a" }}>
                      <input
                        type="checkbox"
                        checked={!!row.convertible}
                        onChange={e => setForm(f => ({ ...f, leaves: f.leaves.map((r, i) => i === idx ? { ...r, convertible: e.target.checked } : r) }))}
                      />
                      Convertible to cash at end of year
                    </label>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, leaves: [...f.leaves, { name: "", days: "", convertible: false }] }))}
                  style={{ background: "#fff", border: "1.5px dashed #d8cfc0", borderRadius: 10,
                           padding: "8px 12px", cursor: "pointer", fontWeight: 600, color: "#5a4e3a", width: "100%" }}>
                  + Add Leave
                </button>
              </>
            );
          })()}

          {/* Earnings */}
          <div className={styles.modalSection}>Earnings</div>
          {form.earnings.length === 0 && (
            <div style={{ fontSize: 12, color: "#8a8070", marginBottom: 8 }}>
              No additional earnings. Click "+ Add Earning" to add an incentive, allowance, or bonus.
            </div>
          )}
          {form.earnings.map((row, idx) => (
            <div key={`earn-${idx}`} style={{ display: "grid", gridTemplateColumns: "1fr 140px 36px", gap: 8, marginBottom: 8 }}>
              <input
                className={styles.input}
                placeholder="Transport allowance"
                value={row.name}
                onChange={e => setForm(f => ({ ...f, earnings: f.earnings.map((r, i) => i === idx ? { ...r, name: e.target.value } : r) }))}
              />
              <input
                className={styles.input}
                type="number" min="0"
                placeholder="Amount"
                value={row.value}
                onChange={e => setForm(f => ({ ...f, earnings: f.earnings.map((r, i) => i === idx ? { ...r, value: e.target.value } : r) }))}
              />
              <button
                type="button"
                title="Remove"
                onClick={() => setForm(f => ({ ...f, earnings: f.earnings.filter((_, i) => i !== idx) }))}
                style={{ background: "#fee2e2", border: "1px solid #fecaca",
                         color: "#991b1b", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>×</button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, earnings: [...f.earnings, { name: "", value: "" }] }))}
            style={{ background: "#fff", border: "1.5px dashed #d8cfc0", borderRadius: 10,
                     padding: "8px 12px", cursor: "pointer", fontWeight: 600, color: "#5a4e3a", width: "100%" }}>
            + Add Earning
          </button>

          {/* Payroll Deductions */}
          <div className={styles.modalSection}>Payroll Deductions</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#8a8070",
                        letterSpacing: ".5px", textTransform: "uppercase", marginBottom: 6 }}>
            Statutory
          </div>
          <div className={styles.modalGrid}>
            <div className={styles.field}>
              <label className={styles.label}>SSS</label>
              <input
                className={styles.input}
                type="number" min="0"
                value={form.deductions.sss}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, sss: e.target.value } }))}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>PhilHealth</label>
              <input
                className={styles.input}
                type="number" min="0"
                value={form.deductions.philhealth}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, philhealth: e.target.value } }))}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Pag-IBIG</label>
              <input
                className={styles.input}
                type="number" min="0"
                value={form.deductions.pagibig}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, pagibig: e.target.value } }))}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Insurance</label>
              <input
                className={styles.input}
                type="number" min="0"
                value={form.deductions.insurance}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, insurance: e.target.value } }))}
              />
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#8a8070",
                        letterSpacing: ".5px", textTransform: "uppercase", margin: "10px 0 6px" }}>
            Other Deductions
          </div>
          {form.deductions.others.map((row, idx) => (
            <div key={`ded-${idx}`} style={{ display: "grid", gridTemplateColumns: "1fr 140px 36px", gap: 8, marginBottom: 8 }}>
              <input
                className={styles.input}
                placeholder="Loan repayment"
                value={row.name}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, others: f.deductions.others.map((r, i) => i === idx ? { ...r, name: e.target.value } : r) } }))}
              />
              <input
                className={styles.input}
                type="number" min="0"
                placeholder="Amount"
                value={row.value}
                onChange={e => setForm(f => ({ ...f, deductions: { ...f.deductions, others: f.deductions.others.map((r, i) => i === idx ? { ...r, value: e.target.value } : r) } }))}
              />
              <button
                type="button"
                title="Remove"
                onClick={() => setForm(f => ({ ...f, deductions: { ...f.deductions, others: f.deductions.others.filter((_, i) => i !== idx) } }))}
                style={{ background: "#fee2e2", border: "1px solid #fecaca",
                         color: "#991b1b", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>×</button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, deductions: { ...f.deductions, others: [...f.deductions.others, { name: "", value: "" }] } }))}
            style={{ background: "#fff", border: "1.5px dashed #d8cfc0", borderRadius: 10,
                     padding: "8px 12px", cursor: "pointer", fontWeight: 600, color: "#5a4e3a", width: "100%" }}>
            + Add Other Deduction
          </button>

          {/* Personal Information */}
          <div className={styles.modalSection}>Personal Information</div>
          <div className={styles.modalGrid}>
            <div className={styles.field}>
              <label className={styles.label}>Birthday</label>
              <DatePicker
                value={form.birthday}
                onChange={v => set("birthday", v)}
                max={new Date().toISOString().slice(0, 10)}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Phone Number</label>
              <input className={styles.input} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="e.g. +63 900 000 000" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <input className={styles.input} value={form.email} onChange={e => set("email", e.target.value)} placeholder="e.g. harry@company.com" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Citizenship</label>
              <input className={styles.input} value={form.citizenship} onChange={e => set("citizenship", e.target.value)} placeholder="e.g. Singaporean" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>City</label>
              <input className={styles.input} value={form.city} onChange={e => set("city", e.target.value)} placeholder="e.g. Singapore" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Address</label>
              <input className={styles.input} value={form.address} onChange={e => set("address", e.target.value)} placeholder="e.g. Tanjong Pagar" />
            </div>
          </div>

          {/* Documents */}
          <div className={styles.modalSection}>Documents</div>
          <div className={styles.docsGrid}>
            {DOC_TYPES.map(dt => {
              const existing = existingDocs.find(d => d.name === dt.label);
              const staged   = docFiles[dt.key];
              // Use backend proxy so download works regardless of Cloudinary access settings
              const docUrl   = existing?.id
                ? `${API_BASE}/api/employees/${emp.code}/documents/${existing.id}/download`
                : null;
              return (
                <div key={dt.key} className={styles.docCard}>
                  <div className={styles.docCardIcon} style={{ background: dt.color }}>{dt.tag}</div>
                  <div className={styles.docCardInfo}>
                    <div className={styles.docCardLabel}>{dt.label}</div>
                    {staged
                      ? <div className={styles.docCardFile}>{staged.name} <button className={styles.docRemove} onClick={() => removeDoc(dt.key)}>✕</button></div>
                      : docUrl
                        ? <div className={styles.docCardFile}>
                            <span>{existing.size}</span>
                            <a href={docUrl} download className={styles.docLink}>⬇ Download</a>
                          </div>
                        : <div className={styles.docCardEmpty}>No file uploaded</div>
                    }
                  </div>
                  <label className={styles.docUploadBtn} title={`Upload ${dt.label}`}>
                    ↑
                    <input type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={e => onDocChange(dt.key, e)} />
                  </label>
                </div>
              );
            })}
          </div>

          {/* Other Documents */}
          <div className={styles.modalSection}>Other Documents</div>
          <div className={styles.docsGrid}>
            {existingOtherDocs.map((doc, i) => {
              const docUrl = doc.id ? `${API_BASE}/api/employees/${emp.code}/documents/${doc.id}/download` : null;
              return (
                <div key={`existing-other-${i}`} className={styles.docCard}>
                  <div className={styles.docCardIcon} style={{ background: "#7f8c8d" }}>{(doc.name || "?").charAt(0).toUpperCase()}</div>
                  <div className={styles.docCardInfo}>
                    <div className={styles.docCardLabel}>{doc.name}</div>
                    {docUrl
                      ? <div className={styles.docCardFile}>
                          <span>{doc.size}</span>
                          <a href={docUrl} download className={styles.docLink}>⬇ Download</a>
                        </div>
                      : <div className={styles.docCardEmpty}>No file</div>
                    }
                  </div>
                </div>
              );
            })}
            {otherDocs.map((od, idx) => (
              <div key={`other-${idx}`} className={styles.docCard}>
                <div className={styles.docCardIcon} style={{ background: od.name ? "#7f8c8d" : "#bbb" }}>
                  {od.name ? od.name.charAt(0).toUpperCase() : "?"}
                </div>
                <div className={styles.docCardInfo}>
                  <input
                    className={styles.input}
                    value={od.name}
                    onChange={e => updateOtherDoc(idx, "name", e.target.value)}
                    placeholder="Document name"
                    style={{ marginBottom: 4, fontSize: "12px", padding: "4px 8px" }}
                  />
                  {od.file
                    ? <div className={styles.docCardFile}>{od.file.name} <button className={styles.docRemove} onClick={() => updateOtherDoc(idx, "file", null)}>✕</button></div>
                    : <div className={styles.docCardEmpty}>No file uploaded</div>
                  }
                </div>
                <label className={styles.docUploadBtn} title="Upload file">
                  ↑
                  <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) updateOtherDoc(idx, "file", f); }} />
                </label>
                <button className={styles.docRemove} onClick={() => removeOtherDoc(idx)} title="Remove" style={{ fontSize: "14px" }}>✕</button>
              </div>
            ))}
          </div>
          <button type="button" className={styles.addDocBtn} onClick={addOtherDoc}>+ Add Document</button>
        </div>

        {error && <div className={styles.modalError}>{error}</div>}

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={save} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Staff"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function PeoplePage() {
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [dept, setDept]                 = useState("All Departments");
  const [sortCol, setSortCol]           = useState(null);
  const [sortDir, setSortDir]           = useState("asc");
  const [modalEmp, setModalEmp]         = useState(undefined); // undefined = closed, null = add, obj = edit

  function loadEmployees() {
    setLoading(true);
    fetch(`${API_BASE}/api/employees`)
      .then(r => r.json())
      .then(data => { setAllEmployees(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => {
    loadEmployees();
  }, []);

  // Faculty→Department merge: every staff member now has a single
  // `department` string, including teachers (whose academic level used
  // to live on `facultyId`). The migration backfilled this on startup.
  const departments = useMemo(() => {
    const set = new Set();
    allEmployees.forEach(e => {
      if (e.department) set.add(e.department);
    });
    return ["All Departments", ...Array.from(set).sort()];
  }, [allEmployees]);

  const filtered = useMemo(() => {
    let list = allEmployees;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.role || "").toLowerCase().includes(q) ||
        (e.department || "").toLowerCase().includes(q)
      );
    }
    if (dept !== "All Departments") list = list.filter(e => e.department === dept);
    if (sortCol) {
      list = [...list].sort((a, b) => {
        const av = (a[sortCol] ?? "").toString().toLowerCase();
        const bv = (b[sortCol] ?? "").toString().toLowerCase();
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [allEmployees, search, dept, sortCol, sortDir]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortArrow = col => sortCol === col ? (sortDir === "asc" ? " ▴" : " ▾") : " ▾";

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Admin · People</div>
          <h1 className={styles.title}>Staff</h1>
          <p className={styles.sub}>
            Faculty and staff records — position, department, salary, status, and contact details.
          </p>
        </div>
      </header>

      <div className={styles.card}>
        {/* Top bar — search, department filter, and the single primary
            action. The previous in-card "Staff" title + duplicate ＋ icon
            button were removed; the H1 above is the page label and the
            labelled "+ Add Staff" button is the only call to action. */}
        <div className={styles.topBar}>
          <div className={styles.searchWrap}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="9" r="6"/><path d="M15 15l-3.5-3.5"/>
            </svg>
            <input className={styles.searchInput} placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select
            className={styles.deptSelect}
            value={dept}
            onChange={e => setDept(e.target.value)}>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <button className={styles.addBtn} onClick={() => setModalEmp(null)}>＋ Add Staff</button>
        </div>

        {/* Table */}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thCheck}><span className={styles.checkBox} /></th>
                <th className={styles.th}>Name</th>
                <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleSort("role")}>Position{sortArrow("role")}</th>
                <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleSort("department")}>Department{sortArrow("department")}</th>
                <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleSort("status")}>Status{sortArrow("status")}</th>
                <th className={`${styles.th} ${styles.sortable}`} onClick={() => toggleSort("salary")}>Salary{sortArrow("salary")}</th>
                <th className={styles.th} />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className={styles.emptyCell}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className={styles.emptyCell}>No staff found.</td></tr>}
              {filtered.map((emp, i) => (
                <tr key={emp.code || i} className={styles.tr}>
                  <td className={styles.tdCheck}><span className={styles.checkBox} /></td>
                  <td className={styles.tdEmp}>
                    {emp.photoUrl
                      ? <img src={emp.photoUrl} alt={emp.name} className={styles.avatarImg} />
                      : <div className={styles.avatar} style={{ background: avatarColor(emp.name) }}>{initials(emp.name)}</div>
                    }
                    <div>
                      <div className={styles.empName}>{emp.name}</div>
                      <div className={styles.empRole}>{emp.role}</div>
                    </div>
                  </td>
                  <td className={styles.td}>{emp.role}</td>
                  <td className={styles.td}>{emp.department || "—"}</td>
                  <td className={styles.td}><StatusBadge status={emp.status} /></td>
                  <td className={styles.td}>{emp.salary != null ? `$${Number(emp.salary).toLocaleString()}` : "—"}</td>
                  <td className={styles.tdEdit}>
                    <button className={styles.editBtn} title="Edit" onClick={() => setModalEmp(emp)}>✎</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit / Add Modal */}
      {modalEmp !== undefined && (
        <EmployeeModal
          emp={modalEmp}
          onClose={() => setModalEmp(undefined)}
          onSaved={() => { setModalEmp(undefined); loadEmployees(); }}
        />
      )}
    </div>
  );
}